<script setup lang="js">
import axios from "axios";
import { defineProps, onMounted, ref, watch } from "vue";
import { Formatter } from "@/utilities/Formatter";

const props = defineProps({
  start: {
    type: String,
    required: false,
    default: null
  },
  end: {
    type: String,
    required: false,
    default: null
  }
});

const page = ref(0);
const pageSize = ref(15);
const result = ref(null);
const loading = ref(false);
const error = ref(false);

const load = async () => {
  try {
    result.value = null;
    error.value = false;
    loading.value = true;
    const response = await axios.get(
      `${window.location.origin}/ui/v3/dashboard/rating?start=${props.start}&end=${props.end ?? ""}&page=${page.value}&count=${pageSize.value}`
    );
    result.value = response.data;
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
};

onMounted(load);
watch(() => props.start, () => {
  page.value = 0;
  load();
});
watch(() => props.end, () => {
  page.value = 0;
  load();
});
watch(page, load);
</script>

<template>
  <section class="client-rating">
    <div class="client-rating__header">
      <div>
        <div class="client-rating__eyebrow">{{ $filters.localize("Dashboard.rating.eyebrow") }}</div>
        <h3 class="client-rating__title">{{ $filters.localize("Dashboard.rating.title") }}</h3>
      </div>
    </div>

    <template v-if="result?.items?.length">
      <div class="client-rating__table-wrap">
        <table class="client-rating__table">
          <thead>
            <tr>
              <th>#</th>
              <th>{{ $filters.localize("Dashboard.rating.client") }}</th>
              <th>{{ $filters.localize("Dashboard.rating.ordersCount") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.profit") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in result.items" :key="item.contactId">
              <td>{{ index + 1 + page * pageSize }}</td>
              <td>{{ item.contactName }}</td>
              <td>{{ item.count }}</td>
              <td>{{ Formatter.toCurrency(item.profit) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <nav class="client-rating__pagination">
        <button class="btn btn-sm btn-outline-secondary" @click="page--" :disabled="page === 0">
          {{ $filters.localize("Dashboard.pagination.back") }}
        </button>
        <span>{{ $filters.localize("Dashboard.pagination.page") }} {{ page + 1 }} {{ $filters.localize("Dashboard.pagination.of") }} {{ Math.max(1, Math.ceil(result.totalPages)) }}</span>
        <button
          class="btn btn-sm btn-outline-secondary"
          @click="page++"
          :disabled="(page + 1) * pageSize >= result.totalCount"
        >
          {{ $filters.localize("Dashboard.pagination.next") }}
        </button>
      </nav>
    </template>

    <template v-else-if="error">
      <div class="dashboard-state dashboard-state--error">
        {{ $filters.localize("Dashboard.states.ratingError") }}
      </div>
    </template>

    <template v-else-if="!loading">
      <div class="dashboard-state dashboard-state--empty">
        {{ $filters.localize("Dashboard.states.empty") }}
      </div>
    </template>

    <template v-else>
      <div class="dashboard-skeleton dashboard-skeleton--table">
        <div v-for="index in 6" :key="index" class="dashboard-skeleton__row"></div>
      </div>
    </template>

    <div v-if="loading && result?.items?.length" class="dashboard-loader-overlay">
      <div class="dashboard-loader"></div>
    </div>
  </section>
</template>

<style scoped>
.client-rating {
  position: relative;
  padding: 1.5rem;
  border: 1px solid rgba(23, 50, 77, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 22px 60px rgba(23, 50, 77, 0.08);
  overflow: hidden;
}

.client-rating__header {
  margin-bottom: 1rem;
}

.client-rating__eyebrow {
  margin-bottom: 0.35rem;
  color: #bb6c31;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  font-size: 0.74rem;
  font-weight: 700;
}

.client-rating__title {
  margin: 0;
  color: #17324d;
  font-size: 1.5rem;
}

.client-rating__table-wrap {
  overflow-x: auto;
}

.client-rating__table {
  width: 100%;
  min-width: 560px;
  border-collapse: separate;
  border-spacing: 0;
}

.client-rating__table th,
.client-rating__table td {
  padding: 0.95rem 0.8rem;
  border-bottom: 1px solid rgba(23, 50, 77, 0.08);
  text-align: left;
}

.client-rating__table th {
  color: #6f8194;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.client-rating__table td {
  color: #17324d;
}

.client-rating__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
  color: #6f8194;
  font-weight: 600;
}

.dashboard-state {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  border-radius: 22px;
  text-align: center;
  font-weight: 600;
}

.dashboard-state--error {
  border: 1px dashed rgba(138, 91, 91, 0.24);
  background: rgba(255, 246, 246, 0.92);
  color: #8a5b5b;
}

.dashboard-state--empty {
  border: 1px dashed rgba(23, 50, 77, 0.14);
  background: rgba(248, 251, 254, 0.9);
  color: #6f8194;
}

.dashboard-skeleton--table {
  display: grid;
  gap: 0.65rem;
}

.dashboard-skeleton__row {
  height: 52px;
  border-radius: 18px;
  background: linear-gradient(90deg, #edf4fb 0%, #f8fbfe 50%, #edf4fb 100%);
  background-size: 200% 100%;
  animation: dashboard-shimmer 1.5s infinite linear;
  border: 1px solid rgba(23, 50, 77, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

.dashboard-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(2px);
}

.dashboard-loader {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 3px solid rgba(23, 50, 77, 0.12);
  border-top-color: #17324d;
  animation: dashboard-spin 0.8s linear infinite;
}

@keyframes dashboard-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@keyframes dashboard-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767px) {
  .client-rating {
    padding: 1rem;
  }

  .client-rating__pagination {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
